import axios from 'axios';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { AxiosHeaders } from 'axios';
import {
    SystemInfo,
    Registration,
    DashboardItem,
    Ticket,
    CreateTicketRequest,
    TicketStatus,
    AddTicketMessageRequest,
    AssigneeOption,
    UpdateRegistrationRequest,
    CreateRegistrationRequest,
    DiagnosticStatus,
    DiagnosticSnapshot,
} from '../types';
import { InstallerReleaseNote } from '../data/installerReleaseNotes';
import { apiScopes, loginRequest, silentRedirectUri } from '../authConfig';
import { msalInstance } from '../authClient';
import { getEasyAuthToken, getGoogleIdToken } from '../googleAuth';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:7071/api';
const INSTALLER_RELEASE_NOTES_URL = (process.env.REACT_APP_INSTALLER_RELEASE_NOTES_URL || '').trim();
const INSTALLER_RELEASE_NOTES_FALLBACK_PATH = '/installer-release-notes.json';

const api = axios.create({
    baseURL: API_BASE_URL,
});

function normalizeInstallerReleaseNotes(data: unknown): InstallerReleaseNote[] {
    if (!Array.isArray(data)) {
        throw new Error('Installer release notes must be an array.');
    }

    const normalized = data
        .filter((item): item is Record<string, unknown> => Boolean(item) && typeof item === 'object')
        .map((item) => {
            const version = typeof item.version === 'string' ? item.version.trim() : '';
            const releasedOn = typeof item.releasedOn === 'string' ? item.releasedOn.trim() : '';
            const highlights = Array.isArray(item.highlights)
                ? item.highlights.filter((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0)
                : [];

            return {
                version,
                releasedOn,
                highlights,
            };
        })
        .filter((item) => item.version && item.releasedOn && item.highlights.length > 0);

    if (normalized.length === 0) {
        throw new Error('Installer release notes payload does not contain valid entries.');
    }

    return normalized;
}

async function fetchInstallerReleaseNotesFromUrl(url: string): Promise<InstallerReleaseNote[]> {
    const response = await fetch(url, {
        method: 'GET',
        headers: {
            Accept: 'application/json',
        },
        cache: 'no-store',
    });

    if (!response.ok) {
        throw new Error(`Failed to fetch installer release notes from ${url} (${response.status}).`);
    }

    const payload = await response.json();
    return normalizeInstallerReleaseNotes(payload);
}

let redirectInProgress = false;
const SESSION_EXPIRED_MESSAGE = 'Session expired. Please sign out and sign in again.';

function getErrorMessage(error: unknown): string {
    if (error instanceof Error) {
        return error.message;
    }

    return String(error ?? '');
}

function isHashEmptyError(error: unknown): boolean {
    const message = getErrorMessage(error).toLowerCase();
    return message.includes('hash_empty_error');
}

function isExpiredTokenResponse(status?: number, message?: string): boolean {
    const normalizedMessage = (message ?? '').toLowerCase();
    return (
        status === 401 &&
        (normalizedMessage.includes('idx10223') || normalizedMessage.includes('token is expired'))
    );
}

async function resolveAccessToken(forceRefresh = false): Promise<string | null> {
    const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0] ?? null;
    if (!account) {
        // No MSAL account – prefer EasyAuth token from Google login exchange.
        return getEasyAuthToken() ?? getGoogleIdToken();
    }

    if (!msalInstance.getActiveAccount()) {
        msalInstance.setActiveAccount(account);
    }

    try {
        const tokenResponse = await msalInstance.acquireTokenSilent({
            account,
            scopes: apiScopes,
            forceRefresh,
            redirectUri: silentRedirectUri,
        });
        return tokenResponse.idToken || null;
    } catch (error) {
        if (isHashEmptyError(error)) {
            // Hidden-iframe redirects can occasionally lose their hash. Retry once with a forced refresh.
            if (!forceRefresh) {
                return resolveAccessToken(true);
            }

            throw new Error(SESSION_EXPIRED_MESSAGE);
        }

        if (!(error instanceof InteractionRequiredAuthError)) {
            throw error;
        }

        if (redirectInProgress) {
            return null;
        }

        redirectInProgress = true;

        await msalInstance.acquireTokenRedirect({
            ...loginRequest,
            scopes: apiScopes,
            account,
        });

        return null;
    }
}

api.interceptors.request.use(async (config) => {
    const accessToken = await resolveAccessToken();
    if (accessToken) {
        const headers = AxiosHeaders.from(config.headers);
        const easyAuthToken = getEasyAuthToken();
        if (easyAuthToken && accessToken === easyAuthToken) {
            headers.delete('Authorization');
            headers.set('X-ZUMO-AUTH', easyAuthToken);
        } else {
            headers.delete('X-ZUMO-AUTH');
            headers.set('Authorization', `Bearer ${accessToken}`);
        }
        config.headers = headers;
    }
    return config;
});

api.interceptors.response.use(
    (response) => response,
    async (error) => {
        if (!axios.isAxiosError(error) || !error.config) {
            throw error;
        }

        const originalRequest = error.config as typeof error.config & {
            _retriedWithFreshToken?: boolean;
        };

        const status = error.response?.status;
        const responseMessage = typeof error.response?.data === 'string'
            ? error.response.data
            : (error.response?.data as any)?.message ?? '';
        const isExpiredTokenError = isExpiredTokenResponse(status, String(responseMessage));
        const shouldRetryWithFreshToken = status === 401 && !originalRequest._retriedWithFreshToken;

        if (shouldRetryWithFreshToken) {
            originalRequest._retriedWithFreshToken = true;
            const freshToken = await resolveAccessToken(true);

            if (freshToken) {
                const headers = AxiosHeaders.from(originalRequest.headers);
                const easyAuthToken = getEasyAuthToken();
                if (easyAuthToken && freshToken === easyAuthToken) {
                    headers.delete('Authorization');
                    headers.set('X-ZUMO-AUTH', easyAuthToken);
                } else {
                    headers.delete('X-ZUMO-AUTH');
                    headers.set('Authorization', `Bearer ${freshToken}`);
                }
                originalRequest.headers = headers;
                return api.request(originalRequest);
            }

            throw new Error(SESSION_EXPIRED_MESSAGE);
        }

        if (isExpiredTokenError) {
            throw new Error(SESSION_EXPIRED_MESSAGE);
        }

        if (isHashEmptyError(error)) {
            throw new Error(SESSION_EXPIRED_MESSAGE);
        }

        throw error;
    }
);

export const fetchSystemReports = async (serialNumber?: string, limit: number = 100): Promise<SystemInfo[]> => {
    try {
        const response = await api.get('/GetSystemReports', {
            params: {
                serialNumber,
                limit,
            },
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching system reports:', error);
        throw error;
    }
};

export const fetchRegistrations = async (): Promise<Registration[]> => {
    try {
        const response = await api.get('/registrations');
        return response.data;
    } catch (error) {
        console.error('Error fetching registrations:', error);
        throw error;
    }
};

export const fetchRegistration = async (serialNumber: string): Promise<Registration> => {
    try {
        const response = await api.get(`/registrations/${encodeURIComponent(serialNumber)}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching registration:', error);
        throw error;
    }
};

export const createRegistration = async (
    payload: CreateRegistrationRequest
): Promise<Registration> => {
    try {
        const response = await api.post('/registrations', payload);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            const status = error.response.status;
            const data = error.response.data as any;
            const message = data?.message || (typeof data === 'string' ? data : null);

            if (status === 403) {
                throw new Error('Access denied: only owners can create registrations.');
            } else if (isExpiredTokenResponse(status, message ?? undefined)) {
                throw new Error(SESSION_EXPIRED_MESSAGE);
            } else if (status === 401) {
                throw new Error('Unauthorized request. Please sign in again.');
            } else if (message) {
                throw new Error(message);
            }

            throw new Error(`Request failed with status ${status}`);
        }
        console.error('Error creating registration:', error);
        throw error instanceof Error ? error : new Error('Failed to create registration');
    }
};

export const updateRegistration = async (
    serialNumber: string,
    payload: UpdateRegistrationRequest
): Promise<Registration> => {
    try {
        const response = await api.patch(
            `/registrations/${encodeURIComponent(serialNumber)}`,
            payload
        );
        return response.data;
    } catch (error) {
        // Extract backend error message if available
        if (axios.isAxiosError(error) && error.response) {
            const status = error.response.status;
            const data = error.response.data as any;
            console.log(`API Error [${status}]:`, data);
            const message = data?.message || (typeof data === 'string' ? data : null);
            console.log('Extracted message:', message);
            
            if (status === 403) {
                throw new Error(`Access denied: ${message || 'You don\'t have admin permissions to modify registrations.'}`);
            } else if (isExpiredTokenResponse(status, message ?? undefined)) {
                throw new Error(SESSION_EXPIRED_MESSAGE);
            } else if (status === 401) {
                throw new Error('Unauthorized request. Please sign in again.');
            } else if (status === 500) {
                throw new Error(message || 'Unable to save registration right now. If this keeps happening, contact an administrator.');
            } else if (message) {
                throw new Error(message);
            } else {
                throw new Error(`Request failed with status ${status}`);
            }
        }
        console.error('Error updating registration:', error);
        throw error instanceof Error ? error : new Error('Failed to save registration');
    }
};

export const fetchDashboard = async (): Promise<DashboardItem[]> => {
    try {
        const response = await api.get('/dashboard');
        return response.data;
    } catch (error) {
        console.error('Error fetching dashboard:', error);
        throw error;
    }
};

export const fetchSystemReportById = async (id: string): Promise<SystemInfo> => {
    try {
        const response = await api.get(`/reports/${encodeURIComponent(id)}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching system report by id:', error);
        throw error;
    }
};

export const fetchTickets = async (): Promise<Ticket[]> => {
    try {
        const response = await api.get('/tickets');
        return response.data;
    } catch (error) {
        console.error('Error fetching tickets:', error);
        throw error;
    }
};

export const fetchTicketAdminUsers = async (): Promise<AssigneeOption[]> => {
    try {
        const response = await api.get('/tickets/admin-users');
        // Support both legacy string[] and new {email,name}[] format
        const data: unknown[] = response.data;
        return data.map((item) =>
            typeof item === 'string'
                ? { email: item, name: item }
                : (item as AssigneeOption)
        );
    } catch (error) {
        console.error('Error fetching ticket admin users:', error);
        return [];
    }
};

export const createTicket = async (payload: CreateTicketRequest): Promise<Ticket> => {
    try {
        const response = await api.post('/tickets', payload);
        return response.data;
    } catch (error) {
        console.error('Error creating ticket:', error);
        throw error;
    }
};

export const updateTicket = async (
    id: string,
    payload: { status?: TicketStatus; assignedTo?: string; notes?: string }
): Promise<Ticket> => {
    try {
        const response = await api.patch(`/tickets/${encodeURIComponent(id)}`, payload);
        return response.data;
    } catch (error) {
        console.error('Error updating ticket:', error);
        throw error;
    }
};

export const addTicketMessage = async (
    id: string,
    payload: AddTicketMessageRequest
): Promise<Ticket> => {
    try {
        const response = await api.post(`/tickets/${encodeURIComponent(id)}/messages`, payload);
        return response.data;
    } catch (error) {
        console.error('Error adding ticket message:', error);
        throw error;
    }
};

export const createAgentCommand = async (
    serialNumber: string,
    payload: { type: string; parameters?: Record<string, unknown> }
): Promise<{ id: string; status: string; type: string; createdAtUtc: string }> => {
    try {
        const response = await api.post(
            `/agents/${encodeURIComponent(serialNumber)}/commands`,
            payload
        );
        return response.data;
    } catch (error) {
        console.error('Error creating agent command:', error);
        throw error;
    }
};

export const queueLatestInstallerCommand = async (
    serialNumber: string
): Promise<{ id: string; status: string; type: string; createdAtUtc: string }> => {
    try {
        const response = await api.post(
            `/agents/${encodeURIComponent(serialNumber)}/commands/install-latest`
        );
        return response.data;
    } catch (error) {
        console.error('Error creating latest installer command:', error);
        throw error;
    }
};

export const getAgentCommand = async (
    serialNumber: string,
    commandId: string
): Promise<{
    id: string;
    type: string;
    status: string;
    exitCode?: number;
    output?: string;
    createdAtUtc: string;
    startedAtUtc?: string;
    completedAtUtc?: string;
}> => {
    try {
        const response = await api.get(
            `/agents/${encodeURIComponent(serialNumber)}/commands/by-id/${encodeURIComponent(commandId)}`
        );
        return response.data;
    } catch (error) {
        console.error('Error fetching agent command:', error);
        throw error;
    }
};

export const getLatestAgentCommand = async (
    serialNumber: string
): Promise<{
    id: string;
    type: string;
    status: string;
    exitCode?: number;
    output?: string;
    createdAtUtc: string;
    startedAtUtc?: string;
    completedAtUtc?: string;
} | null> => {
    try {
        const response = await api.get(
            `/agents/${encodeURIComponent(serialNumber)}/commands/latest?type=RunPackage`
        );

        if (!response.data) {
            return null;
        }

        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 404) {
            return null;
        }

        console.error('Error fetching latest agent command:', error);
        throw error;
    }
};

export const queueDiagnostics = async (serialNumber: string): Promise<void> => {
    await api.post(`/agents/${encodeURIComponent(serialNumber)}/diagnostics`);
};

export const fetchDiagnosticStatus = async (serialNumber: string, signal?: AbortSignal): Promise<DiagnosticStatus> => {
    const response = await api.get<DiagnosticStatus>(`/agents/${encodeURIComponent(serialNumber)}/diagnostics`, { signal });
    return response.data;
};

export const fetchDiagnosticSnapshot = async (
    serialNumber: string, commandId: string, signal?: AbortSignal
): Promise<DiagnosticSnapshot> => {
    const response = await api.get<DiagnosticSnapshot>(
        `/agents/${encodeURIComponent(serialNumber)}/diagnostics/${encodeURIComponent(commandId)}`, { signal });
    return response.data;
};

export const fetchInstallerReleaseNotes = async (): Promise<InstallerReleaseNote[]> => {
    const urlsToTry = [
        INSTALLER_RELEASE_NOTES_URL,
        INSTALLER_RELEASE_NOTES_FALLBACK_PATH,
    ].filter((url, index, array) => Boolean(url) && array.indexOf(url) === index);

    let lastError: unknown = null;

    for (const url of urlsToTry) {
        try {
            return await fetchInstallerReleaseNotesFromUrl(url);
        } catch (error) {
            lastError = error;
            console.warn('Unable to load installer release notes from URL:', url, error);
        }
    }

    if (lastError instanceof Error) {
        throw lastError;
    }

    throw new Error('No installer release notes URL configured.');
};

export const fetchTicketMessageImage = async (ticketId: string, messageId: string): Promise<string> => {
    const response = await api.get(
        `/tickets/${encodeURIComponent(ticketId)}/messages/${encodeURIComponent(messageId)}/image`,
        { responseType: 'blob' }
    );
    return URL.createObjectURL(response.data);
};